import axiosInstance from '../../../fetchConfig/axiosConfig.js';
import { toast } from '../../../common/toast';
import { usePersistedDataStore } from '../../store';
import { calculateProprietyWorkup } from '../SummaryCaseTable/services/summaryRowCreationService.js';
import { getCaseStrengthPercentage } from '../../../utils/caseStrengthCalculator';
import { determineCompLimit, getDefaultCompLimitForReportType } from './createSaveObject';
import { findCompIndex, getCompIdAtIndex } from '../common/compSheetLookup.js';
import { sheetForParcel, sheetSubjectId } from '../CaseReviewHelpers/refreshCaseComps';
import logger from '../../../utils/logger';

const TOP_ALGORITHM_COMPS = 5;

const sheetForCase = (sheets, item, index) => {
  const atIndex = sheets?.[index];
  if (item?.parcel_id == null || sheetSubjectId(atIndex) === String(item.parcel_id)) return atIndex;
  return sheetForParcel(sheets, item.parcel_id).sheet || atIndex;
};

/** Sheet adjusted sale (adj_price, or adj_IFMV on assessment). Index 0 is the subject. */
const roundedAdjAt = (sheet, index, reportType) => {
  if (index == null || index < 0) return null;
  const field = reportType === 'assessment' ? 'adj_IFMV' : 'adj_price';
  const n = parseFloat(sheet?.[field]?.[index]);
  if (!Number.isFinite(n) || n === 0) return null;
  return Math.round(n);
};

const orderedCompIds = (comps) => {
  if (!comps) return [];
  const raw = typeof comps === 'string'
    ? (() => { try { return JSON.parse(comps); } catch { return null; } })()
    : comps;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter((id) => id != null && id !== '');
  return Object.entries(raw)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, id]) => id)
    .filter((id) => id != null && id !== '');
};

/** Adj sales for the comps being saved. Regular-view index when we have one, else optimized. */
const adjPricesForSavedComps = (comps, regularSheet, optimizedSheet, reportType, regularIndexes, optimizedIndexes) => {
  const ids = orderedCompIds(comps);
  if (!ids.length) return null;
  const prices = ids.map((id, i) => {
    const regIdx = Array.isArray(regularIndexes) ? regularIndexes[i] : findCompIndex(regularSheet, id, reportType);
    const fromReg = regIdx != null && regIdx >= 0 ? roundedAdjAt(regularSheet, regIdx, reportType) : null;
    if (fromReg != null) return fromReg;
    const optIdx = Array.isArray(optimizedIndexes) ? optimizedIndexes[i] : findCompIndex(optimizedSheet, id, reportType);
    return optIdx != null && optIdx >= 0 ? roundedAdjAt(optimizedSheet, optIdx, reportType) : null;
  });
  return prices.some((price) => price != null) ? prices : null;
};

/**
 * Top 5 optimize-set comps, plus adjusted-sale snapshots saved with them.
 * FirstRegCompVal: adj sale of regular-view comp index 1.
 * FirstOptimCompVal: adj sale of optimized-view comp index 1.
 * AlgorithmCompVals: those adj sales, same order as AlgorithmComps.
 * SelectedCompVals is not included here. Only a manual save writes it.
 */
export const algorithmFieldsFromOptimized = (optimizedSheet, reportType = 'standard', regularSheet = null) => {
  const FirstRegCompVal = roundedAdjAt(regularSheet, 1, reportType);
  const FirstOptimCompVal = roundedAdjAt(optimizedSheet, 1, reportType);
  const empty = {
    AlgorithmComps: null,
    AlgorithmWorkup: null,
    AlgorithmCompVals: null,
    FirstRegCompVal,
    FirstOptimCompVal,
  };
  if (!optimizedSheet?.parcel_id?.length) return empty;
  const ranked = [];
  for (let i = 1; i < optimizedSheet.parcel_id.length && ranked.length < TOP_ALGORITHM_COMPS; i++) {
    const id = getCompIdAtIndex(optimizedSheet, i, reportType);
    if (id != null && id !== '') ranked.push({ id, index: i });
  }
  if (!ranked.length) return empty;
  const AlgorithmComps = {};
  ranked.forEach(({ id }, index) => { AlgorithmComps[index + 1] = id; });
  const workup = calculateProprietyWorkup({ Comps: AlgorithmComps }, optimizedSheet);
  const AlgorithmWorkup = (typeof workup === 'number' && !isNaN(workup) && workup > 0) ? Math.round(workup) : null;
  const AlgorithmCompVals = ranked.map(({ index }) => roundedAdjAt(optimizedSheet, index, reportType));
  return { AlgorithmComps, AlgorithmWorkup, AlgorithmCompVals, FirstRegCompVal, FirstOptimCompVal };
};

/**
 * Low-level API call to save comps to backend
 * @param {Object} saveObject - The save object to send to backend
 * @returns {Promise<Object>} - Response data from backend
 */
const saveComps = async (saveObject) => {
  try {
    const saveCompsResponse = await axiosInstance.post('/update_comparables', saveObject);
    if (saveObject.compUpdate) {
      toast.success('Comparables saved successfully.');
    }
    return saveCompsResponse.data;
  } catch (error) {
    logger.error(error);
    throw error;
  }
};

/**
 * Safely get RepId value, returning null for invalid/empty values
 * @param {any} repId - The RepId value
 * @returns {any|null} - RepId value or null if invalid/empty
 */
const getRepId = (repId) => {
  if (repId === null || repId === undefined || repId === '') {
    return null;
  }
  return repId;
};

/**
 * Omits heavy or redundant keys (cases, uids) from the attached NegotiationObj.
 */
const prepareNegotiationObjForSave = (negotiationObj, manualReviewFlag) => {
  if (!negotiationObj || typeof negotiationObj !== 'object') {
    return manualReviewFlag === 0 || manualReviewFlag === 1 ? { manualReview: manualReviewFlag } : {};
  }

  const { cases, uids, ...rest } = negotiationObj;
  if (manualReviewFlag === 0 || manualReviewFlag === 1) {
    return { ...rest, manualReview: manualReviewFlag };
  }
  return rest;
};

/**
 * Normalizes CaseNotes value: converts blank strings to null
 * This should be used whenever CaseNotes is saved to the store or database
 * @param {any} caseNotes - The CaseNotes value
 * @returns {any|null} - CaseNotes value or null if blank/empty
 */
export const normalizeCaseNotes = (caseNotes) => {
  if (caseNotes === null || caseNotes === undefined || caseNotes === '') {
    return null;
  }
  // If it's a string, trim it and return null if empty after trimming
  if (typeof caseNotes === 'string' && caseNotes.trim() === '') {
    return null;
  }
  return caseNotes;
};

/**
 * This is for compatibility with the old and now gross casenotes you have (getnegotiation obj matinains case info, but your casenotes are seperate)
 * You just extract from both, prioritizing the 
 * */
export const resolveCaseNotesForCase = (caseIndex, explicitCaseNotes, fallbackCase) => {
  if (explicitCaseNotes !== undefined) {
    return normalizeCaseNotes(explicitCaseNotes);
  }
  const storedNotes = usePersistedDataStore.getState().caseNotes?.[caseIndex];
  if (storedNotes !== undefined) {
    return normalizeCaseNotes(storedNotes);
  }
  return normalizeCaseNotes(fallbackCase?.CaseNotes);
};

/**
 * Unified batch save comps utility - handles all comp saving scenarios
 * 
 * @param {Object} params - Save parameters
 * @param {Object} params.negotiationObj - Full negotiation object
 * @param {Array} params.cases - Array of case objects (can be single case for manual save)
 * @param {Object} params.user - User object
 * @param {Object} params.options - Save options
 * @param {number} params.options.manualReviewFlag - 0 for auto-review, 1 for manual review (default: 0)
 * @param {number} params.options.manualOverride - 0 or 1 (default: 0)
 * @param {number} params.options.townNegotiationCompsSave - Set 1 only from NegotiationTable save (ComparablesWK.Type Town). Otherwise 0.
 * @param {boolean} params.options.manualCompSave - Manual comp save. SelectedCompVals is the adj prices of the comps being saved. Other saves omit it.
 * @param {Object} params.options.trainingCompsByCase - Training comps grouped by case (optional, for training overwrite)
 * @param {boolean} params.options.isTrainingOverwrite - Whether this is a training comp overwrite (default: false)
 * @param {Object} params.options.saveData - Data for saves, keyed by case index (optional)
 * @note compLimit is determined from advancedSettings or dynamic default by report type (20 assessment, 10 others)
 * @param {Object} params.options.saveData[index] - Save data for case at index
 * @param {Object} params.options.saveData[index].compsObject - Pre-built comps object
 * @param {Array} params.options.saveData[index].selectedIndexRegular - Pre-calculated regular indices
 * @param {Array} params.options.saveData[index].selectedIndexOptimized - Pre-calculated optimized indices
 * @param {string} params.options.saveData[index].address - Address (optional, for manual save)
 * @param {number} params.options.saveData[index].repId - RepId (optional)
 * @returns {Promise<Object>} - Save result
 */
export const batchSaveComps = async ({
  negotiationObj,
  cases,
  user,
  options = {}
}) => {
  logger.log('[SaveComps/batchSaveComps] invoked', {
    casesCount: Array.isArray(cases) ? cases.length : 0,
    hasSaveData: !!options?.saveData,
    isTrainingOverwrite: !!options?.isTrainingOverwrite,
    townNegotiationCompsSave: options?.townNegotiationCompsSave ? 1 : 0,
  });
  try {
    const {
      manualReviewFlag = 0,
      manualOverride = 0,
      trainingCompsByCase = null,
      isTrainingOverwrite = false,
      saveData = null,
      townNegotiationCompsSave = 0,
      manualCompSave = false,
    } = options;

    const getReportType = usePersistedDataStore.getState().reportType || negotiationObj.reportType || 'standard';
    const getCompSheet = usePersistedDataStore.getState().compSheet || [];
    const getOptimizedComps = usePersistedDataStore.getState().optimizedComps || [];
    
    // If training overwrite, use training comps grouped by case
    if (isTrainingOverwrite && trainingCompsByCase) {
      // Determine compLimit using shared helper
      let advancedSettings = {};
      try {
        advancedSettings = JSON.parse(localStorage.getItem('advancedSettings')) || {};
      } catch (error) {
        logger.error('Error parsing advancedSettings:', error);
      }
      
      const compLimit = determineCompLimit(null, advancedSettings, getReportType);
      
      return await saveTrainingCompsBatch({
        negotiationObj,
        cases,
        user,
        trainingCompsByCase,
        compLimit,
        getReportType
      });
    }
    
    // Build save object - unified logic using saveData
    const saveObject = {
      ManualReviewFlag: manualReviewFlag,
      ManualOverride: manualOverride,
      TownNegotiationCompsSave: townNegotiationCompsSave ? 1 : 0,
      Cases: cases.map((item, index) => {
        // Find the original index in negotiationObj.cases by matching parcel_id
        const originalCaseIndex = negotiationObj.cases?.findIndex(c => c.parcel_id === item.parcel_id) ?? index;
        const regularSheet = sheetForCase(getCompSheet, item, originalCaseIndex);
        const optimizedSheet = sheetForCase(getOptimizedComps, item, originalCaseIndex);
        const algorithmFields = algorithmFieldsFromOptimized(optimizedSheet, getReportType, regularSheet);
        
        // Use saveData if provided for this case index
        if (saveData && saveData[index]) {
          const caseSaveData = saveData[index];
          
          const caseItemWithComps = {
            ...item,
            Comps: caseSaveData.compsObject
          };
          const compSheetToUse = sheetForCase(getCompSheet, item, originalCaseIndex);
          const workupValue = calculateProprietyWorkup(caseItemWithComps, compSheetToUse);
          const workupValueAverage = (typeof workupValue === 'number' && !isNaN(workupValue) && workupValue > 0) ? Math.round(workupValue) : null;
          
          // Calculate case strength A
          const caseStrengthData = getCaseStrengthPercentage(originalCaseIndex, getReportType);
          const caseStrengthA = caseStrengthData?.caseStrengthA ?? null;
          
          return {
            Subject: item.parcel_id,
            Address: caseSaveData.address || item.Address || '',
            Comps: caseSaveData.compsObject,
            RepId: getRepId(caseSaveData.repId) ?? getRepId(item.RepID?.[0]) ?? getRepId(item.RepId) ?? null,
            SelectedIndexRegular: caseSaveData.selectedIndexRegular || null,
            SelectedIndexOptimized: caseSaveData.selectedIndexOptimized || null,
            WorkupValue: workupValueAverage,
            CaseStrength: caseStrengthA,
            ...algorithmFields,
            ...(manualCompSave ? {
              SelectedCompVals: adjPricesForSavedComps(
                caseSaveData.compsObject,
                regularSheet,
                optimizedSheet,
                getReportType,
                caseSaveData.selectedIndexRegular,
                caseSaveData.selectedIndexOptimized,
              ),
            } : {}),
            CaseNotes: resolveCaseNotesForCase(originalCaseIndex, caseSaveData.caseNotes, item),
            ManualReview: manualReviewFlag,
            UserId: user.userId
          };
        }
        
        // Fallback: use existing Comps from case if no saveData provided
        const existingComps = typeof item.Comps === 'string' 
          ? JSON.parse(item.Comps) 
          : item.Comps || {};
        
        // Determine compLimit for fallback case (when saveData not provided)
        let advancedSettings = {};
        try {
          advancedSettings = JSON.parse(localStorage.getItem('advancedSettings')) || {};
        } catch (error) {
          logger.error('Error parsing advancedSettings:', error);
        }
        
        const fallbackCompLimit = determineCompLimit(null, advancedSettings, getReportType);
        
        // Limit to compLimit
        const limitedComps = {};
        Object.entries(existingComps)
          .sort(([keyA], [keyB]) => Number(keyA) - Number(keyB))
          .slice(0, fallbackCompLimit)
          .forEach(([key, pid]) => {
            limitedComps[key] = pid;
          });
        
        const caseItemWithComps = {
          ...item,
          Comps: limitedComps
        };
        const compSheetToUse = sheetForCase(getCompSheet, item, originalCaseIndex);
        const workupValue = calculateProprietyWorkup(caseItemWithComps, compSheetToUse);
        const workupValueAverage = (typeof workupValue === 'number' && !isNaN(workupValue) && workupValue > 0) ? Math.round(workupValue) : null;
        
        // Calculate case strength A for fallback case
        const caseStrengthData = getCaseStrengthPercentage(originalCaseIndex, getReportType);
        const caseStrengthA = caseStrengthData?.caseStrengthA ?? null;
        
        return {
          Subject: item.parcel_id,
          Comps: limitedComps,
          RepId: getRepId(item.RepID?.[0]) ?? getRepId(item.RepId) ?? null,
          ManualReview: manualReviewFlag,
          UserId: user.userId,
          SelectedIndexRegular: null, // Cannot calculate without comp arrays
          SelectedIndexOptimized: null, // Cannot calculate without comp arrays
          WorkupValue: workupValueAverage,
          CaseStrength: caseStrengthA,
          ...algorithmFields,
          ...(manualCompSave ? {
            SelectedCompVals: adjPricesForSavedComps(
              limitedComps,
              regularSheet,
              optimizedSheet,
              getReportType,
            ),
          } : {}),
          CaseNotes: resolveCaseNotesForCase(originalCaseIndex, undefined, item)
        };
      }),
      UserId: user.userId,
      TrainingFlag: user?.userAccess?.level === 'Training' ? 1 : 0,
      ReportType: getReportType,
      MuniCode: negotiationObj.MuniCode,
      TaxYear: parseInt(negotiationObj.TaxYear),
    };
    const negotiationSnapshot = prepareNegotiationObjForSave(negotiationObj, manualReviewFlag);
    if (negotiationSnapshot != null) {
      saveObject.NegotiationObj = negotiationSnapshot;
    }

    const result = await saveComps(saveObject);
    return result;
  } catch (error) {
    logger.error('Error in batchSaveComps:', error);
    throw error;
  }
};

/**
 * Save training comps batch - groups training comps by case and saves them
 * 
 * @param {Object} params
 * @param {Object} params.negotiationObj - Full negotiation object
 * @param {Array} params.cases - Array of case objects
 * @param {Object} params.user - User object
 * @param {Object} params.trainingCompsByCase - Training comps grouped by parcel_id: { [parcel_id]: { Comps: {...}, ... } }
 * @param {number} params.compLimit - Maximum number of comps to save
 * @param {string} params.getReportType - Report type
 * @returns {Promise<Object>} - Save result
 */
const saveTrainingCompsBatch = async ({
  negotiationObj,
  cases,
  user,
  trainingCompsByCase,
  compLimit = undefined,
  getReportType
}) => {
  const effectiveCompLimit = compLimit ?? getDefaultCompLimitForReportType(negotiationObj?.reportType ?? getReportType);
  try {
    // Build save object with training comps grouped by case
    // Track which cases are being saved to map results back correctly
    const casesToSave = [];
    const saveCases = cases
      .map((item, originalIndex) => {
        const parcelId = item.parcel_id;
        const trainingData = trainingCompsByCase[parcelId];
        
        // If this case has training comps, use them
        if (trainingData && trainingData.Comps) {
          // Sort comps by index and limit to compLimit
          const compsObject = {};
          Object.entries(trainingData.Comps)
            .sort(([keyA], [keyB]) => Number(keyA) - Number(keyB))
            .slice(0, effectiveCompLimit)
            .forEach(([key, pid]) => {
              if (pid) {
                compsObject[key] = pid;
              }
            });
          
          // Track this case for result mapping
          casesToSave.push({ parcelId, originalIndex });
          const store = usePersistedDataStore.getState();
          const regularSheet = sheetForCase(store.compSheet || [], item, originalIndex);
          const optimizedSheet = sheetForCase(store.optimizedComps || [], item, originalIndex);
          const algorithmFields = algorithmFieldsFromOptimized(optimizedSheet, getReportType, regularSheet);

          return {
            Subject: parcelId,
            Comps: compsObject,
            RepId: getRepId(trainingData.RepId) ?? getRepId(item.RepID?.[0]) ?? getRepId(item.RepId) ?? null,
            ManualReview: 0,
            UserId: user.userId,
            SelectedIndexRegular: null, // Training comps may not align with regular/optimized arrays
            SelectedIndexOptimized: null,
            ...algorithmFields,
            CaseNotes: resolveCaseNotesForCase(originalIndex, undefined, item)
          };
        }
        
        // Case has no training comps - return null to skip
        return null;
      })
      .filter(caseItem => caseItem !== null); // Remove cases without training comps
    
    const saveObject = {
      ManualReviewFlag: 0,
      ManualOverride: 1,
      Cases: saveCases,
      UserId: user.userId,
      TrainingFlag: user?.userAccess?.level === 'Training' ? 1 : 0,
      ReportType: getReportType,
      MuniCode: negotiationObj.MuniCode,
      TaxYear: parseInt(negotiationObj.TaxYear),
    };
    const trainingNegotiationSnapshot = prepareNegotiationObjForSave(negotiationObj, 0);
    if (trainingNegotiationSnapshot != null) {
      saveObject.NegotiationObj = trainingNegotiationSnapshot;
    }
    
    // Only save if there are cases with training comps
    if (saveObject.Cases.length === 0) {
      logger.warn('No cases with training comps to save');
      return { results: [], casesToSave: [] };
    }
    
    const result = await saveComps(saveObject);
    
    // Attach the mapping for result processing
    return { ...result, casesToSave };
  } catch (error) {
    logger.error('Error in saveTrainingCompsBatch:', error);
    throw error;
  }
};

/**
 * Group training comps by case (parcel_id)
 * 
 * @param {Array} cases - Array of case objects
 * @returns {Object} - Training comps grouped by parcel_id
 */
export function groupTrainingCompsByCase(cases) {
  const grouped = {};
  
  try {
    cases.forEach((c) => {
      const parcelId = c?.parcel_id;
      if (!parcelId) return;
      
      const tc = c?.TrainingComps;
      if (!Array.isArray(tc) || tc.length === 0) return;
      
      // Collect all training comp entries for this case
      const mergedComps = {};
      let repId = null;
      
      tc.forEach((entry) => {
        const comps = entry?.Comps || {};
        // Merge comps, keeping the first occurrence of each index
        Object.entries(comps).forEach(([idx, pid]) => {
          if (pid && !mergedComps[idx]) {
            mergedComps[idx] = pid;
          }
        });
        
        // Use RepId from first entry if available
        if (!repId && entry?.RepId) {
          repId = entry.RepId;
        }
      });
      
      if (Object.keys(mergedComps).length > 0) {
        grouped[parcelId] = {
          Comps: mergedComps,
          RepId: repId || c?.RepID?.[0] || c?.RepId || null
        };
      }
    });
  } catch (error) {
    logger.error('Error grouping training comps by case:', error);
  }
  
  return grouped;
}

